import { ReactNode, useMemo } from 'react';
import ProductLabelSplash from '@molecules/ProductLabelSplash/ProductLabelSplash';
import { isOfflineProduct, isOfflinePromotion } from '@helpers/typeGuards/product';
import type {
  AxfoodBasicProductViewModel,
  AxfoodOfflineProductViewModel,
  AxfoodProductDetailsViewModel,
} from '@occ/api-client';

interface UseProductLabelSplashProps {
  product:
    | AxfoodProductDetailsViewModel
    | AxfoodBasicProductViewModel
    | AxfoodOfflineProductViewModel
    | undefined
    | null;
  blockElement?: boolean;
  mixMatchOverride?: ReactNode;
}

/**
 * Priority order:
 * 1. medical
 * 2. age_18
 * 3. segmented (personligt)
 * 4. validUntil (bomb)
 * 5. mix match
 * 6. bargain
 * 7. ecological
 * 8. news
 */
export const useProductLabelSplash = ({
  product,
  blockElement = false,
  mixMatchOverride,
}: UseProductLabelSplashProps) => {
  const promotion = product?.potentialPromotions?.[0];

  return useMemo(() => {
    if (!product) return null;

    const isOffline = isOfflineProduct(product);
    const labels = product.labels;

    // 1. medical
    if (!isOffline && ('isDrugProduct' in product || 'nicotineMedicalProduct' in product)) {
      const { isDrugProduct, nicotineMedicalProduct } = product as AxfoodBasicProductViewModel;
      if (isDrugProduct || nicotineMedicalProduct)
        return <ProductLabelSplash type="medical" blockElement={blockElement} />;
    }

    // 2. age_18
    if (labels?.includes('age_restricted_18')) return <ProductLabelSplash type="age_18" blockElement={blockElement} />;

    // 3. segmented
    if (promotion?.campaignType === 'SEGMENTED') {
      const isPromotionUsed = !!(
        promotion &&
        isOfflinePromotion(promotion) &&
        promotion?.redeemLimit != null &&
        promotion.redeemLimit >= 0 &&
        promotion?.timesUsed &&
        promotion.timesUsed >= promotion.redeemLimit
      );
      return <ProductLabelSplash type="segmented" isPromotionUsed={isPromotionUsed} blockElement={blockElement} />;
    }

    // 4. validUntil (bomb)
    if (promotion?.promotionTheme?.code === 'Z061' && promotion.validUntil)
      return <ProductLabelSplash type="bomb" validUntil={promotion.validUntil} blockElement={blockElement} />;

    // 5. mix match
    if (mixMatchOverride) return mixMatchOverride;

    // 6. bargain
    if (!isOffline && (product as AxfoodBasicProductViewModel).bargainProduct)
      return <ProductLabelSplash type="bargain" blockElement={blockElement} />;

    // 7. ecological
    if (labels?.includes('ecological')) return <ProductLabelSplash type="ecological" blockElement={blockElement} />;

    // 8. news
    if (!isOffline && (product as AxfoodBasicProductViewModel).newsSplashProduct)
      return <ProductLabelSplash type="news" blockElement={blockElement} />;

    return null;
  }, [product, promotion, blockElement, mixMatchOverride]);
};
